import {
  AddIcon,
  Dropdown,
  DropdownMenu,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  LazyLoader,
  Modal,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { type FC, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { useWindowDimensions } from 'utils/dimensions';
import { AppointmentForm } from '../../../components/AppointmentForm/AppointmentForm';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { TeachingTopicForm } from '../../../components/TeachingTopicForm/TeachingTopicForm';
import { TeachingUnit } from '../../../components/TeachingUnit/TeachingUnit';
import { TeachingUnitForm } from '../../../components/TeachingUnitForm/TeachingUnitForm';
import { TeachingUnitNoteForm } from '../../../components/TeachingUnitNoteForm/TeachingUnitNoteForm';
import { TeachingUnitsTree } from '../../../components/TeachingUnitsTree/TeachingUnitsTree';
import { CourseWorkMaterialsForm } from '../../../components/WorkMaterialsForm/CourseWorkMaterialsForm';
import { DesktopContext } from '../../../context/IsDesktop';
import { TeachingUnitContext } from '../../../context/TeachingUnitContext';

const CourseLessonsSubpagePageQuery = graphql(`
  query CourseLessonsSubpagePageData($courseUuid: ID!) {
    teachingUnits(where: { group: { uuid: $courseUuid } }) {
      __typename
      uuid
      organization {
        __typename
        uuid
      }
    }
    teachingTopics(where: { group: { uuid: $courseUuid } }) {
      __typename
      uuid
      topicParentConnection {
        __typename
        edges {
          __typename
        }
      }
    }
  }
`);

type Type = 'teachingUnit' | 'teachingTopic' | 'assignment' | 'workmaterial' | 'appointment' | 'notes';

export const CourseLessonsSubpage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop } = useContext(DesktopContext);
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { getAssignmentPath } = useAssignments({ courseUuid });

  const [{ data }] = useQuery({
    query: CourseLessonsSubpagePageQuery,
    variables: { courseUuid: courseUuid },
  });

  const permissions = usePermissionChecker();

  const { isPhone, isTablet, isLaptop } = useWindowDimensions();
  const isMobile = isPhone || isTablet || isLaptop;

  const { teachingUnitUuid: contextUuid, setTeachingUnitUuid } = useContext(TeachingUnitContext);
  const { teachingUnitUuid: paramsUuid } = useParams<{
    teachingUnitUuid: string;
  }>();

  const [modalOpen, setModalOpen] = useState<Type | null>(null);
  const [workMaterialUuid, setWorkMaterialUuid] = useState<string | null>(null);
  const [appointmentUuid, setAppointmentUuid] = useState<string | null>(null);
  const [parentTopicUuid, setParentTopicUuid] = useState<string | null>(null);
  const [topicUuid, setTopicUuid] = useState<string | null>(null);
  const [isNew, setIsNew] = useState<boolean>(false);

  const teachingTopics = data?.teachingTopics;
  const teachingUnits = data?.teachingUnits;

  const rootTopic = teachingTopics?.find((teachingTopic) => {
    return teachingTopic.topicParentConnection.edges.length === 0;
  });

  const firstFromDB = teachingUnits?.[0]?.uuid;

  const teachingUnitUuidToUse = paramsUuid ? paramsUuid : contextUuid ? contextUuid : firstFromDB ? firstFromDB : '';

  const handleAdd = (type: Type, parentUuid: string) => {
    setIsNew(true);
    setParentTopicUuid(parentUuid);
    setModalOpen(type);
  };

  const handleEdit = (type: Type, uuid: string) => {
    setIsNew(false);
    setTopicUuid(uuid);
    setModalOpen(type);
  };

  const handleClose = () => {
    setIsNew(false);
    setModalOpen(null);
    setWorkMaterialUuid(null);
    setAppointmentUuid(null);
  };

  const organizationUuid = useMemo(() => {
    return teachingUnits?.[0]?.organization?.uuid ?? '';
  }, [teachingUnits]);

  const isGroupEditor = permissions?.isGroupEditor({
    uuid: courseUuid,
    organization: { uuid: organizationUuid },
  });

  const TeachingUnitsTreeCard = (
    <BpCard
      header={{
        headline: t('teachingUnits.title'),
        actions: [
          {
            text: '',
            icon: (
              <Dropdown noPadding trigger={<AddIcon />}>
                <DropdownMenu
                  data={[
                    {
                      label: t('teachingUnits.titleSingular'),
                      onClick: () => {
                        if (!rootTopic) return;
                        handleAdd('teachingUnit', rootTopic.uuid);
                      },
                    },
                    {
                      label: t('teachingTopics.titleSingular'),
                      onClick: () => {
                        if (!rootTopic) return;
                        handleAdd('teachingTopic', rootTopic.uuid);
                      },
                    },
                  ]}
                />
              </Dropdown>
            ),
          },
        ],
      }}
    >
      <Suspense fallback={<LazyLoader forceHeight='30vh' embedded />}>
        <TeachingUnitsTree
          isEditable
          courseUuid={courseUuid}
          activeTeachingUnitUuid={teachingUnitUuidToUse}
          onTeachingUnitSelect={(uuid: string) => {
            setTeachingUnitUuid(uuid);
            navigate(`/courses/${courseUuid}/lessons/${uuid}`);
          }}
          onTeachingUnitAdd={(parentUuid) => {
            if (!rootTopic) return;
            handleAdd('teachingUnit', parentUuid ?? rootTopic.uuid);
          }}
          onTopicAdd={(parentUUid) => handleAdd('teachingTopic', parentUUid)}
          onTopicEdit={(uuid) => handleEdit('teachingTopic', uuid)}
        />
      </Suspense>
    </BpCard>
  );

  function handleAssginmentNavigate(assignmentUuid: string) {
    navigate(getAssignmentPath(assignmentUuid));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies(navigate): ignore
  useEffect(() => {
    navigate(`/courses/${courseUuid}/lessons/${teachingUnitUuidToUse}`);
  }, [courseUuid, teachingUnitUuidToUse]);

  return (
    <BpSubpage isForbidden={!isGroupEditor}>
      <Grid>
        {isMobile && <GridRow>{TeachingUnitsTreeCard}</GridRow>}
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={isMobile ? 12 : 8}>
            {!teachingUnitUuidToUse ? (
              <EmptyState
                forcedHeight='25vh'
                fitParent
                title={t('teachingUnits.noTeachingUnits')}
                iconColor='var(--color-primary-light)'
              />
            ) : (
              <TeachingUnit
                teachingUnitUuid={teachingUnitUuidToUse}
                onTeachingUnitEdit={() => {
                  setModalOpen('teachingUnit');
                }}
                onAssignmentNavigate={(uuid) => handleAssginmentNavigate(uuid)}
                onNewWorkMaterial={() => {
                  setModalOpen('workmaterial');
                }}
                onEventEdit={(eventUuid) => {
                  setAppointmentUuid(eventUuid);
                  setModalOpen('appointment');
                }}
                onWorkMaterialEdit={(workmaterialUuid) => {
                  setWorkMaterialUuid(workmaterialUuid);
                  setModalOpen('workmaterial');
                }}
                onNoteEdit={(_materialUuid) => {
                  setModalOpen('notes');
                }}
                onNewAppointment={() => {
                  setModalOpen('appointment');
                }}
                onAppointmentNavigate={(appointmentUuid: string): void => {
                  navigate(`/courses/${courseUuid}/appointments/${appointmentUuid}`);
                }}
                onWorkMaterialNavigate={(workMaterialUuid: string): void => {
                  navigate(`/courses/${courseUuid}/workmaterials/${workMaterialUuid}`);
                }}
              />
            )}
          </GridColumn>
          {!isMobile && <GridColumn width={4}>{TeachingUnitsTreeCard}</GridColumn>}
        </GridRow>
      </Grid>

      {teachingUnitUuidToUse && (
        <Modal isOpen={modalOpen === 'workmaterial'} onRequestClose={handleClose} hideFooter hideHeader isFormModal>
          <Suspense fallback={<LazyLoader forceHeight='30vh' embedded />}>
            <CourseWorkMaterialsForm
              workMaterialUuid={workMaterialUuid ?? undefined}
              currentTeachingUnitUuid={teachingUnitUuidToUse}
              isModal
              isDesktop={isDesktop}
              onClose={handleClose}
            />
          </Suspense>
        </Modal>
      )}

      {courseUuid && (
        <Modal
          isOpen={modalOpen === 'appointment'}
          onRequestClose={() => handleClose()}
          hideHeader
          hideFooter
          isFormModal
        >
          <Suspense fallback={<LazyLoader forceHeight='30vh' embedded />}>
            <AppointmentForm
              organizationUuid={organizationUuid}
              isModal
              holderUuid={courseUuid}
              conferenceContext='GroupOrCourse'
              onClose={() => handleClose()}
              appointmentUuid={appointmentUuid ?? undefined}
              teachingUnitUuid={teachingUnitUuidToUse}
            />
          </Suspense>
        </Modal>
      )}

      {teachingUnitUuidToUse && (
        <Modal isOpen={modalOpen === 'notes'} onRequestClose={() => handleClose()} title={t('notes.edit')} hideFooter>
          <Suspense fallback={<LazyLoader forceHeight='30vh' embedded />}>
            <TeachingUnitNoteForm onClose={handleClose} teachingUnitUuid={teachingUnitUuidToUse} />
          </Suspense>
        </Modal>
      )}

      {courseUuid && (
        <Modal
          width='s'
          isOpen={modalOpen === 'teachingUnit'}
          onRequestClose={() => handleClose()}
          hideFooter
          title={
            isNew
              ? t('common.createType', {
                  type: t('teachingUnits.titleSingular'),
                })
              : t('common.editType', {
                  type: t('teachingUnits.titleSingular'),
                })
          }
        >
          <Suspense fallback={<LazyLoader forceHeight='30vh' embedded />}>
            <TeachingUnitForm
              teachingUnitUuid={isNew ? null : teachingUnitUuidToUse}
              courseUuid={courseUuid}
              onClose={() => handleClose()}
            />
          </Suspense>
        </Modal>
      )}

      {courseUuid && (
        <Modal
          isOpen={modalOpen === 'teachingTopic'}
          onRequestClose={() => handleClose()}
          width='s'
          title={
            !topicUuid
              ? t('common.createType', {
                  type: t('teachingTopics.titleSingular'),
                })
              : t('common.editType', {
                  type: t('teachingTopics.titleSingular'),
                })
          }
        >
          <TeachingTopicForm
            parentTopicUuid={parentTopicUuid}
            topicUuid={!topicUuid ? null : topicUuid}
            courseUuid={courseUuid}
            onClose={() => handleClose()}
          />
        </Modal>
      )}
    </BpSubpage>
  );
};
